import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  ElementRef,
  Renderer2,
  OnDestroy,
  SimpleChanges,
  OnChanges,
  ViewChild,
} from '@angular/core';
import { OrderableQualifier } from 'ngx-web-api';
import { Animations } from '../../../../animations';
import { ViewportService } from '../../../../core/services/viewport.service';
import { ViewPort } from '../../../../domain/view-port.model';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { PopoverContainerPosition, PopoverArrowPosition } from '../../../../domain/popover-position.enum';
import { SpecialWizardService } from '../../../../core/services/special-wizard.service';
import { Subject } from 'rxjs';
import { ItemEditorService } from 'app/core/services/item-editor.service';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { ScrollEventService } from 'app/shared/scroll-event.service';

@Component({
  selector: 'fts-qualifier-choice',
  templateUrl: './qualifier-choice.component.html',
  styleUrls: ['./qualifier-choice.component.scss'],
  animations: [Animations.scaleX],
})
export class QualifierChoiceComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  orderedQualifiers: string[];
  @Input()
  qualifiers: OrderableQualifier[];
  @Input()
  visible: boolean;
  @Input()
  size: string;
  @Input()
  isHalf: boolean;
  @Input()
  ingredientIndex: number;
  @Input()
  isInModal: boolean;
  @Input()
  popoverContextEl: ElementRef;
  @Input()
  isSecondHalf = false;
  @Input()
  inItemEditorModal: boolean;
  @Input()
  isHalfAndHalf = false;
  @Input()
  hideInHalfAndHalf = false;

  @Output()
  qualifierChange: EventEmitter<OrderableQualifier> = new EventEmitter();

  specialWizardBody: ElementRef;
  popoverContainerPosition = PopoverContainerPosition.Top;
  popoverArrowPosition = PopoverArrowPosition.Right;
  viewportHeight: number;
  viewportTopOffset: number;
  destroy$: Subject<void> = new Subject<void>();
  groupedQualifiers: Array<OrderableQualifier[]>;
  pluralQuals: boolean;

  isMobile = false;
  isTablet = false;
  sideOnly = false;
  faCaretDown = faCaretDown;
  private unlisten: () => void;

  @ViewChild('popTemplate', { static: true })
  public popTemplateRef: ElementRef;

  constructor(
    private host: ElementRef,
    private renderer: Renderer2,
    private specialWizardService: SpecialWizardService,
    private viewportService: ViewportService,
    private editorService: ItemEditorService,
    private scrollService: ScrollEventService
  ) {
    this.unlisten = () => {};
  }

  ngOnInit() {
    if (this.isInModal) {
      this.specialWizardBody = this.specialWizardService.getSpecialWizardBody();
      if (this.specialWizardBody) {
        this.unlisten = this.renderer.listen(this.specialWizardBody.nativeElement, 'scroll', () => {
          this.viewportHeight = this.getViewportHeight();
          this.viewportTopOffset = this.getViewportTopOffset();
        });
      }
    }

    this.viewportService.viewport$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((viewport: ViewPort) => {
      this.isMobile = viewport.isMobile;
      this.isTablet = viewport.isTablet;
      this.computePopoverStyle();
    });

    this.scrollService.throttledScroll$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.viewportHeight = this.getViewportHeight();
      this.viewportTopOffset = this.getViewportTopOffset();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['orderedQualifiers'] || changes['qualifiers']) {
      this.calcGroupedQualifiers();
    }

    if (changes['qualifiers'] && !!this.qualifiers) {
      this.sideOnly = this.editorService.hasSideQualsOnly(this.qualifiers);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unlisten();
  }

  toggleQualifier(qualifier: OrderableQualifier) {
    this.qualifierChange.emit(qualifier);
  }

  toggleSingleQualifier(qualifier: OrderableQualifier[][]) {
    if (qualifier.length === 1) {
      this.qualifierChange.emit(qualifier[0][0]);
    }
  }

  trackByQualifierTypeFn(_index: number, qualifiers: OrderableQualifier[]) {
    return qualifiers[0].type;
  }

  calcGroupedQualifiers() {
    const groups = {};
    (this.qualifiers || []).forEach(qualifier => {
      groups[qualifier.type] = groups[qualifier.type] || [];
      groups[qualifier.type].push(qualifier);
    });

    const sortedGroups = {};
    Object.keys(groups)
      .sort()
      .forEach(key => (sortedGroups[key] = groups[key]));
    this.groupedQualifiers = Object.keys(sortedGroups).map(key => sortedGroups[key]);
    this.pluralQuals = !!this.groupedQualifiers && (this.groupedQualifiers?.length > 1 || this.groupedQualifiers[0].length > 1);
  }

  computePopoverStyle() {
    if (!this.popoverContextEl) {
      this.popoverContextEl = this.host.nativeElement.parentNode;
    }
    this.viewportHeight = this.getViewportHeight();
    this.viewportTopOffset = this.getViewportTopOffset();
  }

  private getViewportHeight() {
    if (this.isInModal) {
      return this.specialWizardBody.nativeElement.getBoundingClientRect().height;
    } else {
      return window.innerHeight;
    }
  }

  private getViewportTopOffset() {
    if (this.isInModal || this.inItemEditorModal) {
      const header = <HTMLElement>document.querySelector('.modal-header');
      const specialWizardHeaderTop = header.offsetTop;
      const specialWizardHeaderHeight = header.offsetHeight;

      return specialWizardHeaderTop + specialWizardHeaderHeight;
    } else {
      const header = <HTMLElement>document.querySelector('fts-header');
      return header.offsetHeight;
    }
  }
}
